// src/components/ParticipantHistoryDialog.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ParticipantHistoryDialog = ({
  open,
  onClose,
  participantHistory,
  onAddParticipants,
  currentParticipants,
}) => {
  const { t } = useTranslation();
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [selectedToReplace, setSelectedToReplace] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedToAdd([]);
      setSelectedToReplace([]);
    }
  }, [open]);

  const availableToAdd = participantHistory.filter(
    (historyParticipant) =>
      !currentParticipants.some(
        (currentParticipant) =>
          currentParticipant.name === historyParticipant.name
      )
  );

  const handleAddToggle = (participant) => {
    setSelectedToAdd((prev) =>
      prev.some((p) => p.name === participant.name)
        ? prev.filter((p) => p.name !== participant.name)
        : [...prev, participant]
    );
  };

  const handleReplaceToggle = (participant) => {
    setSelectedToReplace((prev) =>
      prev.some((p) => p.name === participant.name)
        ? prev.filter((p) => p.name !== participant.name)
        : [...prev, participant]
    );
  };

  const handleSelectAllAdd = () => {
    setSelectedToAdd(
      selectedToAdd.length === availableToAdd.length ? [] : [...availableToAdd]
    );
  };

  const handleSelectAllReplace = () => {
    setSelectedToReplace(
      selectedToReplace.length === currentParticipants.length
        ? []
        : [...currentParticipants]
    );
  };

  const handleConfirm = () => {
    if (selectedToAdd.length > 0 || selectedToReplace.length > 0) {
      onAddParticipants({
        toAdd: selectedToAdd,
        toReplace: selectedToReplace,
      });
      onClose();
    }
  };

  const isAddSelected = (participant) =>
    selectedToAdd.some((p) => p.name === participant.name);
  const isReplaceSelected = (participant) =>
    selectedToReplace.some((p) => p.name === participant.name);

  const totalSelections = selectedToAdd.length + selectedToReplace.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh", // let dialog breathe
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>{t("Manage Participants")}</DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          overflow: "hidden", // keep scroll only in panels
        }}
      >
        {/* Add from History */}
        <Paper
          elevation={2}
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {t("Add from History")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "Select participants from your history to add to the current schedule"
              )}
            </Typography>
            <Button
              size="small"
              onClick={handleSelectAllAdd}
              sx={{ mt: 1 }}
              disabled={availableToAdd.length === 0}
            >
              {selectedToAdd.length === availableToAdd.length
                ? t("Deselect All")
                : t("Select All")}
            </Button>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
            {availableToAdd.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 3 }}
              >
                {t(
                  "All participants from your history are already in the current schedule."
                )}
              </Typography>
            ) : (
              <List dense>
                {availableToAdd.map((participant, index) => (
                  <ListItem
                    key={`add-${participant.name}-${index}`}
                    sx={{ px: 0 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAddSelected(participant)}
                          onChange={() => handleAddToggle(participant)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {participant.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("Max")}: {participant.maxShiftsPerPerson} | {t("Rest")}:{" "}
                            {participant.minEmptyDaysBetweenShifts}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>

        {/* Replace Current */}
        <Paper
          elevation={2}
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" color="error" gutterBottom>
              {t("Remove")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Select current participants to remove from the schedule")}
            </Typography>
            <Button
              size="small"
              onClick={handleSelectAllReplace}
              sx={{ mt: 1 }}
              disabled={currentParticipants.length === 0}
            >
              {selectedToReplace.length === currentParticipants.length
                ? t("Deselect All")
                : t("Select All")}
            </Button>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
            {currentParticipants.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 3 }}
              >
                {t("No current participants to replace.")}
              </Typography>
            ) : (
              <List dense>
                {currentParticipants.map((participant, index) => (
                  <ListItem
                    key={`replace-${participant.name}-${index}`}
                    sx={{ px: 0 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isReplaceSelected(participant)}
                          onChange={() => handleReplaceToggle(participant)}
                          size="small"
                          color="error"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {participant.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("Max")}: {participant.maxShiftsPerPerson} | {t("Rest")}:{" "}
                            {participant.minEmptyDaysBetweenShifts}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>
      </DialogContent>

      {totalSelections > 0 && (
        <Box sx={{ px: 3, pb: 1, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedToAdd.length > 0 && (
              <>
                {t("Adding")}: {selectedToAdd.map((p) => p.name).join(", ")}
              </>
            )}
            {selectedToAdd.length > 0 && selectedToReplace.length > 0 && " | "}
            {selectedToReplace.length > 0 && (
              <>
                {t("Removing")}:{" "}
                {selectedToReplace.map((p) => p.name).join(", ")}
              </>
            )}
          </Typography>
        </Box>
      )}

      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={totalSelections === 0}
        >
          {t("Apply Changes")} ({totalSelections})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParticipantHistoryDialog;
